import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationDialog } from 'components/shared';
import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import RuleSetCard from 'components/shared/RuleSetCard';
import { IndefiniteText } from '../../Protocols.styles';
import {
  getProtocolDurationInWeeks,
  getProtocolValidity
} from '../../Protocols.utils';
import { useDeleteProtocolRule } from '../../hooks/useDeleteProtocolRule';
import { useProtocolRuleDrawer } from '../../hooks/useProtocolRuleDrawer';
import ProtocolDetailToolbar from '../ProtocolDetailToolbar';
import ProtocolRuleDrawer from '../ProtocolRuleDrawer';
import ProtocolRuleGrid from '../ProtocolRuleGrid';
import { EditButton } from './ProtocolDetail.styles';

import type { ProtocolRuleResponse } from 'generated/index';
import type { RuleSetCardStatus } from 'components/shared/RuleSetCard';
import type { ProtocolValidity } from '../../Protocols.types';
import type { ProtocolDetailProps } from './ProtocolDetail.types';

const CARD_STATUS: Record<ProtocolValidity, RuleSetCardStatus> = {
  current: 'active',
  future: 'concept',
  expired: 'expired'
};

/**
 * The validity only colours the card's status bar; there is deliberately no chip with it.
 *
 * A protocol laid out like the plan unit inner page, with the toolbar in the tab header, and as its
 * content the same card as a care receiver's prescription — always open, with the rule grid as its
 * body. Everything comes from the list, rules included, so an edit shows as soon as the list is
 * refetched.
 */
const ProtocolDetail = ({
  protocol,
  protocols,
  onSelect,
  onBack,
  onEdit
}: ProtocolDetailProps) => {
  'use memo';
  const { t } = useTranslation('common');
  const weekCount = getProtocolDurationInWeeks(protocol);
  const ruleDrawer = useProtocolRuleDrawer();
  const { deleteRule } = useDeleteProtocolRule(protocol.id);
  // The rule whose trash can was clicked, until the user confirms or cancels
  const [ruleToDelete, setRuleToDelete] = useState<ProtocolRuleResponse>();

  return (
    <>
      <ProtocolDetailToolbar
        protocol={protocol}
        protocols={protocols}
        onSelect={onSelect}
        onBack={onBack}
      />
      <RuleSetCard
        title={protocol.name}
        status={CARD_STATUS[getProtocolValidity(protocol)]}
        actions={
          <EditButton
            type="pen"
            size="small"
            aria-label={t('edit')}
            onClick={onEdit}
          />
        }
        metaItems={[
          {
            key: 'period',
            icon: 'calendar',
            content: (
              <>
                {dateFormatter(protocol.validFrom)}
                {' - '}
                {protocol.validUntil ? (
                  dateFormatter(protocol.validUntil)
                ) : (
                  <IndefiniteText>
                    {t('adminPortal.protocols.indefinite')}
                  </IndefiniteText>
                )}
              </>
            )
          },
          {
            key: 'duration',
            icon: 'hourglass-half',
            content: t('adminPortal.protocols.detail.durationInWeeks', {
              count: weekCount
            })
          },
          {
            key: 'rules',
            icon: 'clock',
            content: t('adminPortal.protocols.detail.ruleCount', {
              count: protocol.rules.length
            })
          }
        ]}
        size="large"
      >
        <ProtocolRuleGrid
          rules={protocol.rules}
          weekCount={weekCount}
          onAdd={ruleDrawer.openCreate}
          onEdit={ruleDrawer.openEdit}
          onDelete={setRuleToDelete}
        />
      </RuleSetCard>
      <ProtocolRuleDrawer
        open={ruleDrawer.isOpen}
        protocolId={protocol.id}
        rule={ruleDrawer.rule}
        onClose={ruleDrawer.close}
        onSaved={ruleDrawer.close}
      />
      {ruleToDelete && (
        <ConfirmationDialog
          handleAction={(isConfirmed) => {
            setRuleToDelete(undefined);
            if (isConfirmed) {
              deleteRule(ruleToDelete);
            }
          }}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          type="warning"
          title={t('adminPortal.protocols.detail.deleteRuleDialog.title')}
        >
          <p>
            {t('adminPortal.protocols.detail.deleteRuleDialog.body', {
              name: ruleToDelete.activity.name
            })}
          </p>
        </ConfirmationDialog>
      )}
    </>
  );
};

export default ProtocolDetail;
